/**
 * Inbox 通知 payload 补齐（enrich）的纯函数。
 *
 * 背景：Novu inbox list / WS push 的 notification.data 只有 workflow in_app step 里配的 data，
 * 历史上不含完整 trigger payload，前端得再调后端代理 `/pms/notifications/messages` 补。
 * 2026-11-02 起（api 5289d8fd2）员工端 7 个 workflow 的 in_app data 加了：
 *   - `payload`：trigger payload 的镜像（叶子全是字符串，null / 缺省 → 空串 ""）
 *   - `inboxDataVersion: "1"`
 * 带这个版本号的通知直接把 data.payload 合并进 data，不再请求后端。
 *
 * 合并口径（两条路径一致）：`data = { ...data, ...payload }` —— **合并、不替换**。
 * 旧实现把 data 整个换成 message.payload，task-contractor-update 的 `data.url`（跳转用）被抹掉。
 */
import type { Notification } from '@novu/js'

export const INBOX_DATA_VERSION = '1'

type AnyRecord = Record<string, unknown>

const isRecord = (v: unknown): v is AnyRecord => !!v && typeof v === 'object' && !Array.isArray(v)

/** data 里已经带齐 payload 镜像（inboxDataVersion === "1" 且 payload 是对象）。 */
export function hasInlinePayload(notification: Pick<Notification, 'data'> | null | undefined): boolean {
  const data = notification?.data as AnyRecord | undefined
  return !!data && data.inboxDataVersion === INBOX_DATA_VERSION && isRecord(data.payload)
}

/** 后端代理返回的 message（单条 / 批量结构相同），只取用得到的字段。 */
export interface InboxMessage {
  payload?: AnyRecord | null
  templateIdentifier?: string | null
  _templateId?: string | null
  tags?: string[] | null
}

/** payload 的每个键在 data 里都已是同一个值（浅比较）= 已经合并过。 */
function alreadyMerged(data: AnyRecord | undefined, payload: AnyRecord): boolean {
  if (!data) return false
  return Object.keys(payload).every((k) => data[k] === payload[k])
}

/**
 * 把 inline payload（inboxDataVersion 1）合并进 data，返回新对象；不满足条件 / 已合并过
 * 原样返回同一引用（幂等，且不制造新引用 —— 缓存刷新与 UI 替换靠引用判断有没有变化）。
 */
export function applyInlinePayload<T extends Notification>(notification: T): T {
  if (!hasInlinePayload(notification)) return notification
  const data = notification.data as AnyRecord
  const payload = data.payload as AnyRecord
  if (alreadyMerged(data, payload)) return notification
  return { ...notification, data: { ...data, ...payload } } as T
}

/**
 * 把后端 message 补进 notification，返回新对象（不改入参）。
 * - data：`{ ...data, ...message.payload }`，保留 data 里 payload 没有的键（如 contractor 的 url）
 * - workflow：只在 SDK 没给时补，避免覆盖已有的正确字段
 * message 为空 / 没有可补的内容时原样返回同一引用。
 */
export function applyMessage<T extends Notification>(notification: T, message: InboxMessage | null | undefined): T {
  if (!message) return notification
  const anyN = notification as unknown as AnyRecord
  const patch: AnyRecord = {}
  const data = anyN.data as AnyRecord | undefined
  if (isRecord(message.payload) && !alreadyMerged(data, message.payload)) {
    patch.data = { ...(data ?? {}), ...message.payload }
  }
  if (message.templateIdentifier && !anyN.workflow) {
    patch.workflow = {
      id: message._templateId,
      identifier: message.templateIdentifier,
      name: message.templateIdentifier,
      critical: false,
      tags: message.tags || [],
    }
  }
  if (Object.keys(patch).length === 0) return notification
  // Notification 是带 #private 字段的类实例，spread 后丢原型方法；消费方只读数据字段，
  // 与本目录其它乐观更新（{ ...n, isRead }）同一口径。
  return { ...notification, ...patch } as T
}

/** 非空字符串才算"有"——payload 镜像把 null / 缺省渲成 ""，不能拼进 URL。 */
export function nonEmptyString(v: unknown): string | undefined {
  return typeof v === 'string' && v.trim() !== '' ? v : undefined
}

/** 点击跳转的站内地址：data.url 非空才跳。 */
export function notificationUrl(notification: Pick<Notification, 'data'> | null | undefined): string | undefined {
  return nonEmptyString((notification?.data as AnyRecord | undefined)?.url)
}

/**
 * 点击通知：标已读 + 跳转。
 * - data 里已经有可用 url：立刻跳，不等补数据（也不等标已读回来），标已读照常在后面做
 * - 没有 url：补齐（后台补齐还没回来时当场补一次）与标已读并行，补完再取 url，有才跳
 * markRead 由调用方自己吞错（失败只提示，不挡跳转）。
 */
export async function openNotification<T extends Pick<Notification, 'data'>>(
  notification: T,
  deps: {
    ensureEnriched: (notification: T) => Promise<T>
    markRead: () => Promise<void>
    navigate: (url: string) => void
  },
): Promise<void> {
  const knownUrl = notificationUrl(notification)
  if (knownUrl) {
    deps.navigate(knownUrl)
    await deps.markRead()
    return
  }
  const enrichedPromise = deps.ensureEnriched(notification)
  await deps.markRead()
  const url = notificationUrl(await enrichedPromise)
  if (url) deps.navigate(url)
}

/** 批量接口不存在（prod 后端还没上）的判定：404 / 405。兼容原始 axios error 与 typed Error（带 .status）。 */
export function isBatchUnsupportedError(error: unknown): boolean {
  const e = error as { response?: { status?: number }; status?: number } | null | undefined
  const status = e?.response?.status ?? e?.status
  return status === 404 || status === 405
}
